/* ==========================================================================
   <pv-orgnr> - Organisasjonsnummer for faktura til bedrift (2026-10-14)

   Markup + all customer-facing texts: snippets/pv-orgnr-field.liquid
   (cart page + mini-cart, .no only). Loaded once from layout/theme.liquid:
   the mini-cart is re-rendered with innerHTML, where a snippet's own
   <script> never runs.

   Flow:
     1. Strip spaces (and an optional leading "NO" / trailing "MVA").
     2. MOD11 check in the browser (weights 3,2,7,6,5,4,3,2; remainder 0 gives
        check digit 0, remainder 1 is never valid). No lookup when it fails.
     3. Look up https://data.brreg.no/enhetsregisteret/api/enheter/<nr>
        (plain GET, no custom headers, so no CORS preflight).
          404                 -> try /underenheter/<nr>: hit -> sub-unit (never
                                 verified, all five cleared, message names the
                                 parent overordnetEnhet); 404 again -> not found
          410 / slettedato    -> deleted
          konkurs / underAvvikling / underTvangsavviklingEllerTvangsopplosning
                              -> inactive
          other 200           -> verified (ENK too, decided 14.10: «ENK er
                                 ok med forskuddsfaktura»; org_form still set)
          network / 5xx / 429 -> error (never verified)
     4. Write five cart attributes with /cart/update.js. The payment rule
        (proffvolt-cart-rules) only trusts org_verifisert === "1":
          org_nr, org_navn, org_form, org_verifisert, org_sjekket
        "" removes an attribute. Clearing the field clears all five.

   Kun-bedrift carts (gate mode, data-fill-name): the same element is the
   org.nr. input of the kun-bedrift declaration form. pv-cart-rules.js keeps
   writing b2b_firmanavn / b2b_orgnr / b2b_bekreftet from that form exactly as
   before; this script adds the five org_* attributes and fills the firmanavn
   input with the name from Brønnøysund (never over a name the customer typed;
   an auto-filled name is cleared again when the number has no hit).

   Safety:
     - The input is never attributes[...] and never tied to the cart form, so a
       form POST can never set org_nr alone.
     - As soon as the typed number differs from a verified number on the cart,
       all five attributes are cleared at once (not debounced), so a quick
       click on "Til kassen" never carries a stale verification.
     - Network or cart errors never set org_verifisert.
     - Checkout is never blocked.
   ========================================================================== */

(function () {
  if (!window.customElements || customElements.get('pv-orgnr')) return;

  var BRREG_URL = 'https://data.brreg.no/enhetsregisteret/api/enheter/';
  var BRREG_SUB_URL = 'https://data.brreg.no/enhetsregisteret/api/underenheter/';
  var UPDATE_URL = '/cart/update.js';
  var KEYS = ['org_nr', 'org_navn', 'org_form', 'org_verifisert', 'org_sjekket'];
  var DEBOUNCE_MS = 600;
  var TIMEOUT_MS = 8000;

  var ICON_OK = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M3 8.5l3.2 3L13 4.5"/></svg>';
  var ICON_ERR = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" focusable="false"><circle cx="8" cy="8" r="6.6"/><path d="M8 4.6v4.2"/><path d="M8 11.3v.1"/></svg>';

  var cache = {};          // digits -> lookup result (per page load)
  var cartAttrs = null;    // the five attributes as we believe the cart holds them
  var lastSent = null;     // our own last successful write (the only basis for skipping one)
  var pendingWrites = 0;
  var writeChain = Promise.resolve();
  var seq = 0;             // newest check wins; stale responses are ignored
  var view = null;         // shared display state { digits, kind, navn }
  var instances = [];

  function empty() {
    return { org_nr: '', org_navn: '', org_form: '', org_verifisert: '', org_sjekket: '' };
  }

  function normalize(raw) {
    var s = String(raw || '').replace(/[\s  ]+/g, '');
    return s.replace(/^NO(?=\d)/i, '').replace(/(\d)MVA$/i, '$1');
  }

  function mod11(d) {
    if (!/^\d{9}$/.test(d)) return false;
    var w = [3, 2, 7, 6, 5, 4, 3, 2];
    var sum = 0;
    for (var i = 0; i < 8; i++) sum += Number(d.charAt(i)) * w[i];
    var r = sum % 11;
    var check = r === 0 ? 0 : 11 - r;
    if (check === 10) return false; // remainder 1: no valid check digit
    return check === Number(d.charAt(8));
  }

  function classify(j) {
    var navn = String((j && j.navn) || '').trim();
    var form = String((j && j.organisasjonsform && j.organisasjonsform.kode) || '').trim();
    var inactive = !!(j && (j.slettedato || j.respons_klasse === 'SlettetEnhet' || j.konkurs === true ||
      j.underAvvikling === true || j.underTvangsavviklingEllerTvangsopplosning === true));
    if (inactive) return { kind: 'inactive', navn: navn, form: form };
    // A hit is only verified with both name and form; anything less is treated as a failed lookup.
    if (!navn || !form) throw new Error('brreg: missing navn/organisasjonsform');
    return { kind: 'ok', navn: navn, form: form };
  }

  // Not a main entity: a sub-unit (avdeling/BEDR) number is a common mistake.
  function lookupSubunit(d, opts) {
    return fetch(BRREG_SUB_URL + d, opts).then(function (res) {
      if (res.status === 404 || res.status === 410) return { kind: 'notfound', navn: '', form: '' };
      if (!res.ok) throw new Error('brreg underenheter ' + res.status);
      return res.json().then(function (j) {
        var parent = String((j && j.overordnetEnhet) || '').trim();
        if (!parent) return { kind: 'notfound', navn: '', form: '' };
        return { kind: 'subunit', navn: '', form: '', overordnet: parent };
      });
    });
  }

  function lookup(d) {
    if (cache[d]) return Promise.resolve(cache[d]);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
    var opts = { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined };
    return fetch(BRREG_URL + d, opts)
      .then(function (res) {
        if (res.status === 404) return lookupSubunit(d, opts);
        if (res.status === 410) return { kind: 'inactive', navn: '', form: '' };
        if (!res.ok) throw new Error('brreg ' + res.status);
        return res.json().then(classify);
      })
      .then(function (r) {
        clearTimeout(timer);
        r.checked = new Date().toISOString();
        cache[d] = r;
        return r;
      }, function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  function attrsFor(d, r) {
    var a = empty();
    if (!d || !r) return a;
    if (r.kind === 'subunit') return a;         // a sub-unit is never the buyer: clear all five
    a.org_nr = d;
    if (r.kind === 'error') return a;           // no check completed: nothing else, never verified
    a.org_sjekket = r.checked || new Date().toISOString();
    if (r.kind === 'notfound') return a;
    a.org_navn = r.navn || '';
    a.org_form = r.form || '';
    if (r.kind === 'ok') a.org_verifisert = '1';
    return a;
  }

  function same(a, b) {
    if (!a || !b) return false;
    for (var i = 0; i < KEYS.length; i++) {
      if ((a[KEYS[i]] || '') !== (b[KEYS[i]] || '')) return false;
    }
    return true;
  }

  // Serialised writes: each waits for the previous one. Always all five keys in ONE call,
  // so an old org_verifisert "1" can never survive a change. Skipped only when identical
  // to our own last successful write (never on the strength of the server-rendered snapshot).
  function write(attrs, keepalive) {
    pendingWrites++;
    var p = writeChain.then(function () {
      if (same(lastSent, attrs)) return true;
      return fetch(UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: !!keepalive
      }).then(function (res) {
        if (!res.ok) throw new Error('cart/update.js ' + res.status);
        cartAttrs = Object.assign({}, attrs);
        lastSent = Object.assign({}, attrs);
        return true;
      });
    });
    function done() { pendingWrites--; }
    writeChain = p.then(done, done);
    return p;
  }

  // Gate mode: keep the declaration's firmanavn in step with the looked-up company.
  function syncName(navn) {
    for (var i = 0; i < instances.length; i++) {
      var sel = instances[i].dataset.fillName;
      var t = sel ? document.querySelector(sel) : null;
      if (!t) continue;
      var cur = t.value.trim();
      var auto = t.getAttribute('data-pv-autofill') || '';
      if (cur !== '' && cur !== auto) continue; // typed by the customer: leave it
      if (cur === navn) continue;
      t.value = navn;
      t.setAttribute('data-pv-autofill', navn);
      t.dispatchEvent(new Event('input', { bubbles: true })); // pv-cart-rules.js saves b2b_firmanavn
    }
  }

  function setView(v, source) {
    view = v;
    if (source) source.render();
    for (var i = 0; i < instances.length; i++) {
      if (instances[i] !== source) instances[i].render();
    }
  }

  class PvOrgnr extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.status = this.querySelector('[data-pv-orgnr-status]');
      if (!this.input || !this.status) return;
      if (instances.indexOf(this) === -1) instances.push(this);

      // A freshly server-rendered element carries the cart's real attributes.
      // Only trust them on its first connect and when none of our writes is in flight.
      if (!this._seeded) {
        this._seeded = true;
        if (!pendingWrites) {
          cartAttrs = {
            org_nr: this.dataset.savedNr || '',
            org_navn: this.dataset.savedNavn || '',
            org_form: this.dataset.savedForm || '',
            org_verifisert: this.dataset.savedOk || '',
            org_sjekket: this.dataset.savedSjekket || ''
          };
          if (!same(lastSent, cartAttrs)) lastSent = null; // cart differs from what we last wrote
        }
      }

      if (!this._bound) {
        this._bound = true;
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('change', this.onCommit.bind(this));
        this.input.addEventListener('blur', this.onCommit.bind(this));
        this.input.addEventListener('keydown', this.onKey.bind(this));
      }

      this.restore();
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
      var i = instances.indexOf(this);
      if (i !== -1) instances.splice(i, 1);
    }

    text(kind, v) {
      var t = this.dataset['t' + kind.charAt(0).toUpperCase() + kind.slice(1)] || '';
      if (kind === 'ok') return t.replace('{navn}', (v && v.navn) || '');
      if (kind === 'subunit') return t.replace('{overordnet}', (v && v.overordnet) || '');
      return t;
    }

    // Show the right message after a (re-)render without a new lookup when possible.
    restore() {
      var d = normalize(this.input.value);
      if (!d) { this.render(); return; }
      if (view && view.digits === d) { this.render(); return; }
      var c = cartAttrs || empty();
      if (c.org_nr === d && c.org_sjekket) {
        var kind = c.org_verifisert === '1' && c.org_navn ? 'ok'
          : c.org_navn ? 'inactive'
          : 'notfound';
        view = { digits: d, kind: kind, navn: c.org_navn };
        this.render();
        return;
      }
      this.check(true);
    }

    onInput() {
      var d = normalize(this.input.value);
      // Typed away from a verified number: drop the verification right away.
      if (cartAttrs && cartAttrs.org_verifisert === '1' && d !== cartAttrs.org_nr) {
        seq++;
        write(empty(), true).catch(function () {});
      }
      if (!view || view.digits !== d) setView({ digits: d, kind: 'idle', navn: '' }, this);
      clearTimeout(this._timer);
      this._timer = setTimeout(this.check.bind(this, false), DEBOUNCE_MS);
    }

    onCommit() {
      clearTimeout(this._timer);
      this.check(true);
    }

    onKey(evt) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.onCommit();
      }
    }

    // final: true on blur/change/Enter/restore, false while typing (debounced).
    check(final) {
      clearTimeout(this._timer);
      var self = this;
      var d = normalize(this.input.value);

      if (!d) {
        seq++;
        setView({ digits: '', kind: 'idle', navn: '' }, this);
        write(empty()).catch(function () {});
        syncName('');
        return;
      }

      if (!/^\d{9}$/.test(d) || !mod11(d)) {
        // While typing, stay quiet until there are 9 digits or something that can never be valid.
        var tooEarly = !final && /^\d{1,8}$/.test(d);
        seq++;
        setView({ digits: d, kind: tooEarly ? 'idle' : 'invalid', navn: '' }, this);
        write(empty()).catch(function () {});
        if (!tooEarly) syncName('');
        return;
      }

      if (view && view.digits === d) {
        if (view.kind === 'busy') return;                         // lookup already running
        if (/^(ok|notfound|inactive|subunit)$/.test(view.kind)) {     // already resolved
          this.render();
          return;
        }
      }

      var my = ++seq;
      setView({ digits: d, kind: 'busy', navn: '' }, this);

      lookup(d)
        .then(function (r) {
          if (my !== seq) return;
          return write(attrsFor(d, r)).then(function () {
            if (my !== seq) return;
            setView({ digits: d, kind: r.kind, navn: r.navn || '', overordnet: r.overordnet || '' }, self);
            syncName(r.navn || '');
          });
        })
        .catch(function (err) {
          if (my !== seq) return;
          if (window.console) console.warn('pv-orgnr:', err);
          setView({ digits: d, kind: 'error', navn: '' }, self);
          write(attrsFor(d, { kind: 'error' })).catch(function () {});
          syncName('');
        });
    }

    render() {
      if (!this.input || !this.status) return;
      var v = view || { digits: normalize(this.input.value), kind: 'idle', navn: '' };

      // Keep the other instance (drawer vs cart page) showing the same number.
      if (document.activeElement !== this.input && normalize(this.input.value) !== v.digits) {
        this.input.value = v.digits;
      }

      var kind = v.kind;
      var text = kind === 'idle' ? '' : this.text(kind, v);
      var cls = kind === 'ok' ? 'ok' : kind === 'busy' ? 'busy' : kind === 'idle' ? '' : 'error';
      var icon = kind === 'ok' ? ICON_OK : cls === 'error' ? ICON_ERR : '';

      if (kind === 'invalid' || kind === 'notfound' || kind === 'subunit') this.input.setAttribute('aria-invalid', 'true');
      else this.input.removeAttribute('aria-invalid');

      var st = this.status;
      var key = kind + '|' + text;
      if (st.getAttribute('data-key') === key) return; // unchanged: do not re-announce
      st.setAttribute('data-key', key);
      st.className = 'pv-orgnr__status' + (cls ? ' pv-orgnr__status--' + cls : '');
      while (st.firstChild) st.removeChild(st.firstChild);
      if (!text) return;
      if (icon) {
        var ic = document.createElement('span');
        ic.className = 'pv-orgnr__icon';
        ic.setAttribute('aria-hidden', 'true');
        ic.innerHTML = icon; // static markup only
        st.appendChild(ic);
      }
      var msg = document.createElement('span');
      msg.className = 'pv-orgnr__msg';
      msg.textContent = text; // the company name from brreg is text, never HTML
      st.appendChild(msg);
    }
  }

  customElements.define('pv-orgnr', PvOrgnr);

  // Mini-cart (2026-10-18): the org.nr. field sits folded in <details class="mini-cart__orgnr">
  // in a scrolling footer under sticky buttons. On open, scroll the footer so the field
  // clears the buttons, without pushing the summary line out at the top. 'toggle' does not
  // bubble, so listen in the capture phase (works across drawer re-renders).
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d || !d.classList || !d.classList.contains('mini-cart__orgnr') || !d.open) return;
    var footer = d.closest('.mini-cart__footer');
    var bar = footer && footer.querySelector('.mini-cart__actions');
    if (!footer || !bar) return;
    var r = d.getBoundingClientRect();
    var over = r.bottom - (bar.getBoundingClientRect().top - 8);
    var room = r.top - footer.getBoundingClientRect().top - 8;
    if (over > 0) footer.scrollTop += Math.min(over, Math.max(room, 0));
  }, true);
})();
